import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import Footer from "../components/footer";
import ContactForm from "../components/contactForm";
import Seo from "../components/Seo";
import NotFound from "./notFound";
import { useSchedule, usePages, resolveEventStatus } from "../data/useLocations";
import { dateRangeLabel } from "./events";

const BRAND = "#d0142d";

const WEEK_DAYS = [
  "niedziela", "poniedziałek", "wtorek", "środa",
  "czwartek", "piątek", "sobota",
];

const DATE_STATUS = {
  open: { label: "Wolne miejsca", bg: "#1f7a3d" },
  last: { label: "Ostatnie miejsca", bg: "#b5651d" },
  full: { label: "Brak miejsc", bg: "#7a1111" },
  cancelled: { label: "Odwołany", bg: "#7a1111" },
};

function weekDayOf(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "";
  return WEEK_DAYS[new Date(y, m - 1, d).getDay()] || "";
}

function ageLabel(from, to) {
  if (from == null && to == null) return null;
  if (from != null && to != null) return `${from}-${to} lat`;
  if (from != null) return `od ${from} lat`;
  return `do ${to} lat`;
}

/** "2026-10-25" → "25.10.2026" */
function numDate(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso || "");
  return `${d}.${String(m).padStart(2, "0")}.${y}`;
}

/**
 * Nagłówek terminu, razem z godzinami:
 *   jednodniowy  -> "25.10.2026, niedziela · 10:00 - 12:00"
 *   wielodniowy  -> "24.10.2026, 18:00 - 25.10.2026, 8:00"
 */
function dateHeadline(d) {
  if (!d || !d.date) return "";

  const t = (x) => (x ? String(x).replace(/^0/, "") : "");
  const start = t(d.startTime);
  const end = t(d.endTime);

  if (d.endDate && d.endDate !== d.date) {
    const left = start ? `${numDate(d.date)}, ${start}` : numDate(d.date);
    const right = end ? `${numDate(d.endDate)}, ${end}` : numDate(d.endDate);
    return `${left} - ${right}`;
  }

  const wd = weekDayOf(d.date);
  const head = `${numDate(d.date)}${wd ? `, ${wd}` : ""}`;

  if (!start) return head;
  return end ? `${head} · ${start} - ${end}` : `${head} · ${start}`;
}

/**
 * Opis terminu: w każdej linii fragment do pierwszego dwukropka włącznie
 * dostaje kolor wydarzenia, reszta zostaje czarna.
 */
function renderNote(text, color) {
  return String(text)
    .split("\n")
    .map((line, i) => {
      if (!line.trim()) {
        return <span key={i} style={{ display: "block", height: "0.6em" }} />;
      }

      const idx = line.indexOf(":");
      if (idx === -1) {
        return (
          <span key={i} style={{ display: "block" }}>
            {line}
          </span>
        );
      }

      return (
        <span key={i} style={{ display: "block" }}>
          <span style={{ color: color }}>{line.slice(0, idx + 1)}</span>
          {line.slice(idx + 1)}
        </span>
      );
    });
}

export default function Event() {
  const { slug } = useParams();

  const { data, loading } = useSchedule();
  const { pages } = usePages();

  const events = data?.events || [];
  const event = useMemo(
    () => events.find((e) => e.slug === slug) || null,
    [events, slug]
  );

  /** ID sali → nazwa podstrony, do opisu miejsca terminu. */
  const roomName = useMemo(() => {
    const map = new Map();
    pages.forEach((p) => {
      p.rooms.forEach((r) => {
        map.set(r.id, p.isMulti ? `${p.name} - ${r.label}` : p.name);
      });
    });
    return map;
  }, [pages]);

  const status = event ? resolveEventStatus(event) : null;
  const color = event?.color || BRAND;
  const archived = status === "past";
  const cancelled = status === "cancelled";

  /** Dane dla formularza zgłoszeniowego. */
  const leadLocation = useMemo(() => {
    if (!event) return null;

    const firstWithLocation = (event.dates || []).find((d) => d.locationId);
    const page = firstWithLocation
      ? pages.find((p) => p.roomIds.includes(firstWithLocation.locationId))
      : null;

    return {
      id: firstWithLocation?.locationId ?? null,
      name: event.title,
      slug: event.slug,
      color,
      area: page?.area ?? null,
      interests: [],
    };
  }, [event, pages, color]);

  if (loading) return <EventSkeleton />;
  if (!event) return <NotFound />;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        gap: "50px",
      }}
    >
      <Seo
        title={event.title}
        description={event.lead}
        path={`/${event.slug}`}
        image={event.coverUrl}
        type="article"
        noindex={!event.inSitemap}
      />

      {/* HERO */}
      <div className="carousel-wrapper" style={{ height: "380px" }}>
        <div
          className="carouselImage-wrapper"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.5)), url(${event.coverUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            height: "380px",
            paddingTop: "0px",
          }}
        >
          <h1
            className="item-title"
            style={{
              fontWeight: "bold",
              textShadow:
                "0 2px 6px rgba(0,0,0,0.55), 0 8px 18px rgba(0,0,0,0.55), 0 16px 36px rgba(0,0,0,0.45), 0 28px 60px rgba(0,0,0,0.35)",
            }}
          >
            {event.title}
          </h1>
          {event.subtitle ? (
            <h2
              className="item-desc"
              style={{
                fontWeight: "BOLD",
                textShadow:
                  "0 2px 6px rgba(0,0,0,0.55), 0 8px 18px rgba(0,0,0,0.55), 0 16px 36px rgba(0,0,0,0.45), 0 28px 60px rgba(0,0,0,0.35)",
                marginTop: "-5px",
              }}
            >
              {event.subtitle}
            </h2>
          ) : null}
        </div>
      </div>

      {/* PASEK STANU */}
      <section
        style={{
          width: "92%",
          maxWidth: "1000px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "center",
          marginTop: "-20px",
        }}
      >
        <Pill color={color} filled>{dateRangeLabel(event.dates)}</Pill>
        {ageLabel(event.ageFrom, event.ageTo) ? (
          <Pill color={color}>{ageLabel(event.ageFrom, event.ageTo)}</Pill>
        ) : null}
        {cancelled ? <Pill color="#7a1111" filled>Wydarzenie odwołane</Pill> : null}
        {archived && !cancelled ? <Pill color="#555">To wydarzenie już się odbyło</Pill> : null}
      </section>

      {/* OPIS */}
      {event.description ? (
        <>
          <h2 style={sectionTitle}>O wydarzeniu</h2>
          <section
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "80%",
              maxWidth: "1000px",
            }}
          >
            <p
              style={{
                color: "rgb(30,30,30)",
                fontSize: "15px",
                textAlign: "center",
                whiteSpace: "pre-line",
                lineHeight: 1.65,
                margin: 0,
              }}
            >
              {event.description}
            </p>
          </section>
        </>
      ) : null}

      <hr style={{ border: `2px solid ${color}`, width: "80%", borderRadius: "10px" }} />

      {/* TERMINY */}
      {(event.dates || []).length > 0 ? (
        <>
          <h2 style={{ ...sectionTitle, marginTop: "-25px" }}>Terminy</h2>
          <section
            style={{
              width: "92%",
              maxWidth: "700px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {event.dates.map((d) => {
              const st = DATE_STATUS[d.status] || DATE_STATUS.open;
              const place = d.locationId
                ? roomName.get(d.locationId) || d.locationLabel
                : d.locationLabel;
              const age = ageLabel(d.ageFrom, d.ageTo);
              const info = [age, place].filter(Boolean).join(" · ");

              return (
                <div
                  key={d.id}
                  style={{
                    position: "relative",
                    backgroundColor: "rgb(255,255,255)",
                    border: "1px solid rgba(0,0,0,0.07)",
                    borderLeft: `6px solid ${color}`,
                    borderRadius: "14px",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    opacity: d.status === "cancelled" ? 0.6 : 1,
                  }}
                >
                  {/* STREFA 1 - kiedy i dla kogo */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "10px",
                      padding: "16px 18px 14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        flex: "1 1 220px",
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: 900,
                          color: "rgba(0,0,0,0.88)",
                          lineHeight: 1.3,
                        }}
                      >
                        {dateHeadline(d)}
                      </span>

                      {info ? (
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "rgba(0,0,0,0.7)",
                            lineHeight: 1.45,
                          }}
                        >
                          {info}
                        </div>
                      ) : null}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                        gap: "6px",
                        flex: "0 1 auto",
                      }}
                    >
                      {d.description ? (
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 800,
                            color: color,
                            backgroundColor: `${color}1a`,
                            borderRadius: "999px",
                            padding: "4px 11px",
                            lineHeight: 1.4,
                          }}
                        >
                          {d.description}
                        </span>
                      ) : null}

                      {!archived ? (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 900,
                            color: "rgb(250,250,250)",
                            backgroundColor: st.bg,
                            borderRadius: "999px",
                            padding: "4px 10px",
                            whiteSpace: "nowrap",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {st.label}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* STREFA 2 - ile i co w cenie */}
                  {d.price != null || d.priceNote || d.note ? (
                    <div
                      style={{
                        borderTop: "1px solid rgba(0,0,0,0.07)",
                        backgroundColor: "rgba(0,0,0,0.018)",
                        padding: "13px 18px 15px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "9px",
                      }}
                    >
                      {d.price != null ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "18px",
                              fontWeight: 900,
                              color: "rgba(0,0,0,0.88)",
                              letterSpacing: "-0.01em",
                            }}
                          >
                            {d.price} zł
                          </span>
                          {d.priceNote ? (
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "rgba(0,0,0,0.45)",
                              }}
                            >
                              {d.priceNote}
                            </span>
                          ) : null}
                        </div>
                      ) : d.priceNote ? (
                        <div style={{ fontSize: "14px", fontWeight: 900, color: color }}>
                          {d.priceNote}
                        </div>
                      ) : null}

                      {d.note ? (
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "rgba(0,0,0,0.7)",
                            lineHeight: 1.6,
                          }}
                        >
                          {renderNote(d.note, color)}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {event.signupNote && !archived ? (
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "rgba(0,0,0,0.55)",
                  textAlign: "center",
                  marginTop: "4px",
                }}
              >
                {event.signupNote}
              </p>
            ) : null}
          </section>
        </>
      ) : null}

      {/* SZCZEGÓŁY */}
      {(event.details || []).length > 0 ? (
        <>
          <hr style={{ border: `2px solid ${color}`, width: "80%", borderRadius: "10px" }} />
          <h2 style={{ ...sectionTitle, marginTop: "-25px" }}>Dobrze wiedzieć</h2>
          <section
            style={{
              width: "92%",
              maxWidth: "1000px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "14px",
              justifyContent: "center",
            }}
          >
            {event.details.map((d, i) => (
              <div
                key={i}
                style={{
                  flex: "1 1 260px",
                  minWidth: "260px",
                  backgroundColor: "rgba(255,255,255,0.94)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "18px",
                  padding: "16px 18px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "13px", fontWeight: 900, color: color }}>
                  {d.label}
                </h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.65)",
                    lineHeight: 1.55,
                    whiteSpace: "pre-line",
                  }}
                >
                  {d.text}
                </p>
              </div>
            ))}
          </section>
        </>
      ) : null}

      {/* GALERIA */}
      {(event.gallery || []).length > 0 ? (
        <>
          <hr style={{ border: `2px solid ${color}`, width: "80%", borderRadius: "10px" }} />
          <h2 style={{ ...sectionTitle, marginTop: "-25px" }}>Zdjęcia</h2>
          <section
            style={{
              width: "92%",
              maxWidth: "1200px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "12px",
              justifyContent: "center",
            }}
          >
            {event.gallery.map((src, i) => (
              <a
                key={i}
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: "1 1 280px",
                  maxWidth: "380px",
                  borderRadius: "18px",
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                }}
              >
                <img
                  src={src}
                  alt={`${event.title} - zdjęcie ${i + 1}`}
                  loading="lazy"
                  style={{ width: "100%", height: "220px", objectFit: "cover", display: "block" }}
                />
              </a>
            ))}
          </section>
        </>
      ) : null}

      {/* ZGŁOSZENIE */}
      {!archived && !cancelled ? (
        <>
          <hr style={{ border: `2px solid ${color}`, width: "80%", borderRadius: "10px" }} />
          <h2 style={{ ...sectionTitle, marginTop: "-25px", marginBottom: "-45px" }} id="kontakt">
            Zgłoś się na wydarzenie!
          </h2>
          <section
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "90%",
              maxWidth: "1200px",
            }}
          >
            <ContactForm locationObj={leadLocation} />
          </section>
        </>
      ) : (
        <section
          style={{
            width: "92%",
            maxWidth: "700px",
            backgroundColor: color,
            borderRadius: "22px",
            padding: "28px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "19px", fontWeight: 900, color: "rgb(250,250,250)" }}>
            {cancelled ? "To wydarzenie zostało odwołane" : "To wydarzenie już się odbyło"}
          </h2>
          <p style={{ margin: 0, fontSize: "13px", fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
            Sprawdź, co szykujemy dalej - albo zapisz dziecko na regularne zajęcia.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center" }}>
            <Link to="/wydarzenia" style={ctaLight(color)}>Inne wydarzenia</Link>
            <Link to="/lokalizacje" style={ctaGhost}>Zobacz grafik zajęć</Link>
          </div>
        </section>
      )}

      <hr style={{ border: `2px solid ${color}`, width: "80%", borderRadius: "10px" }} />

      <Link
        to="/wydarzenia"
        style={{
          textDecoration: "none",
          fontSize: "13px",
          fontWeight: 900,
          color: color,
          marginTop: "-25px",
          marginBottom: "20px",
        }}
      >
        ← Wszystkie wydarzenia
      </Link>

      <Footer />
    </div>
  );
}

// ── POMOCNICZE ───────────────────────────────────────────────────────────────

const sectionTitle = {
  color: "rgb(30,30,30)",
  fontSize: "25px",
  fontWeight: "bold",
  textAlign: "center",
  marginBottom: "-25px",
};

const ctaGhost = {
  textDecoration: "none",
  color: "rgb(250,250,250)",
  border: "1px solid rgba(255,255,255,0.6)",
  borderRadius: "999px",
  padding: "11px 20px",
  fontSize: "12px",
  fontWeight: 900,
};

function ctaLight(color) {
  return {
    textDecoration: "none",
    backgroundColor: "rgb(250,250,250)",
    color,
    borderRadius: "999px",
    padding: "11px 20px",
    fontSize: "12px",
    fontWeight: 900,
  };
}

function Pill({ children, color, filled }) {
  return (
    <span
      style={{
        fontSize: "12px",
        fontWeight: 900,
        borderRadius: "999px",
        padding: "7px 14px",
        color: filled ? "rgb(250,250,250)" : color,
        backgroundColor: filled ? color : `${color}1a`,
      }}
    >
      {children}
    </span>
  );
}

function EventSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Wczytywanie wydarzenia"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", gap: "30px" }}
    >
      <div style={{ width: "100%", height: "380px", backgroundColor: "rgba(0,0,0,0.06)" }} />
      {[60, 24, 24].map((h, i) => (
        <span
          key={i}
          style={{
            display: "block",
            height: `${h}px`,
            width: i === 0 ? "40%" : "70%",
            maxWidth: "700px",
            borderRadius: "10px",
            backgroundColor: "rgba(0,0,0,0.05)",
          }}
        />
      ))}
    </div>
  );
}